import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { publicFetch } from '../utils/authenticatedFetch';

/**
 * Pieces shared by the sector views built on `/fear-greed/sectors/history`
 * (the "Over time" line chart and the rotation map): one query, one highlight
 * palette, one pin model -- so a sector pinned blue is blue wherever it is drawn.
 */

const HISTORY_ENDPOINT = 'https://api.1stprinciples.pro/fear-greed/sectors/history';

/**
 * Validated categorical slots (dataviz palette, all-pairs safe for three).
 * Aqua sits below 3:1 on a light surface, which is why every highlighted mark
 * also carries a direct label -- identity is never colour alone.
 */
export const HIGHLIGHT_COLORS = ['#2a78d6', '#eb6834', '#1baf7a'];
export const MAX_HIGHLIGHTS = HIGHLIGHT_COLORS.length;
export const RECESSIVE_STROKE = '#c3c2b7';
export const GRID_INK = '#e1e0d9';
export const AXIS_INK = '#898781';
export const INK = '#111827';
export const MUTED = '#9ca3af';

// re-exported so the sector views keep one import site
export { shortDate } from '../utils/dateLabels';

export const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);

/** Keyed on `days` alone, so two views asking for the same window share one request. */
export const useSectorHistory = (days) => useQuery({
  queryKey: ['sectorHistory', days],
  queryFn: async () => {
    const response = await publicFetch(`${HISTORY_ENDPOINT}?days=${days}`);
    if (!response.ok) throw new Error(`Sector history request failed: ${response.status}`);
    return response.json();
  },
  staleTime: 60 * 60 * 1000,
  retry: 1
});

/**
 * Ordered highlight slots: index is the colour slot, a null slot is free.
 * Colour follows the sector, never its rank -- unpinning one never repaints the others.
 */
export const usePinnedSlots = (defaultPinned = []) => {
  const [slots, setSlots] = React.useState(() =>
    Array.from({ length: MAX_HIGHLIGHTS }, (_, i) => defaultPinned[i] ?? null)
  );
  const slotOf = (sector) => slots.indexOf(sector);
  const togglePin = (sector) => {
    setSlots((current) => {
      const at = current.indexOf(sector);
      if (at !== -1) return current.map((s, i) => (i === at ? null : s));
      const free = current.indexOf(null);
      if (free === -1) return current;
      return current.map((s, i) => (i === free ? sector : s));
    });
  };
  return { slots, slotOf, togglePin, full: slots.every(Boolean) };
};

/** The one loading line every market-map view shows while its query is in flight. */
export const LoadingNote = ({ children }) => (
  <div style={{ fontSize: '0.78rem', color: '#6b7280', padding: '10px 0' }}>{children}</div>
);

export const Pill = ({ active, color, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '5px',
      padding: '2px 9px',
      fontSize: '0.7rem',
      lineHeight: 1.6,
      borderRadius: '999px',
      border: `1px solid ${active ? color || '#374151' : '#d1d5db'}`,
      background: active ? color || '#374151' : '#fff',
      color: active ? '#fff' : '#4b5563',
      cursor: 'pointer'
    }}
  >
    {children}
  </button>
);

/** The row of sector pills that pins / unpins a highlight. */
export const SectorPinPills = ({ sectors, pins }) => (
  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', margin: '8px 0' }}>
    {sectors.map((sector) => {
      const slot = pins.slotOf(sector);
      const pinned = slot !== -1;
      return (
        <Pill
          key={sector}
          active={pinned}
          color={pinned ? HIGHLIGHT_COLORS[slot] : undefined}
          onClick={() => pins.togglePin(sector)}
          title={!pinned && pins.full ? `Unpin a sector first (max ${MAX_HIGHLIGHTS})` : pinned ? 'Unpin' : 'Highlight this sector'}
        >
          {pinned && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />}
          {sector}
        </Pill>
      );
    })}
  </div>
);
